import PropTypes from 'prop-types';
import React from 'react';
import { cx } from 'emotion';

import { analyticsMetadataPropTypes } from 'site-react/core/Analytics';
import MaterialIcon from 'site-react/core/Icon/MaterialIcon';
import Spinner from 'site-react/core/Spinner';
import UnstyledButton from 'site-react/core/UnstyledButton';
import theme from 'site-react/theme';

import {
  buttonStyle,
  blockStyle,
  compactStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  secondaryButtonStyleWhite,
  smallFullWidthStyle,
  hiddenForSpinnerStyle,
  iconStyle,
  iconStyleSmallIconOnly,
  labelStyle,
  smallIconOnlyStyle,
  spinnerWrapperStyle,
} from './Button.styles';

const Button = React.forwardRef(
  (
    {
      analyticsMetadata,
      className,
      elementType,
      form,
      href,
      iconType,
      isBlock,
      isCompact,
      isEnabled,
      isLoading,
      label,
      onClick,
      rel,
      styleAtSmall,
      styleType,
      target,
      testId,
      title,
      type,
    },
    ref,
  ) => {
    let icon = null;

    const iconClassNames = {
      [iconStyle]: true,
      [iconStyleSmallIconOnly]: styleAtSmall === 'iconOnly',
      [hiddenForSpinnerStyle]: isLoading,
    };

    if (iconType) {
      icon = (
        <MaterialIcon
          iconType={iconType}
          className={cx(iconClassNames, 'test-Button-icon')}
        />
      );
    }

    const buttonClassName = cx(
      buttonStyle,
      {
        // `styleAtSmall`s
        [smallFullWidthStyle]: styleAtSmall === 'fullWidth',
        [smallIconOnlyStyle]: styleAtSmall === 'iconOnly',

        // `styleType`s
        [primaryButtonStyle]: styleType === 'primary',
        [secondaryButtonStyle]: styleType === 'secondary',
        [secondaryButtonStyleWhite]: styleType === 'secondaryWhite',

        // other modifiers
        [blockStyle]: isBlock,
        [compactStyle]: isCompact,
      },
      className,
    );

    return (
      <UnstyledButton
        analyticsMetadata={analyticsMetadata}
        className={buttonClassName}
        elementType={elementType}
        form={form}
        href={href}
        isEnabled={isEnabled}
        label={label}
        onClick={onClick}
        ref={ref}
        rel={rel}
        target={target}
        testId={testId}
        title={title}
        type={type}
      >
        {icon}
        <span
          className={cx(
            labelStyle,
            styleAtSmall === 'iconOnly' && theme.utilities.visuallyHiddenSmall,
            isLoading && hiddenForSpinnerStyle,
            'test-Button-text',
          )}
        >
          {label}
        </span>
        {isLoading && (
          <div className={cx(spinnerWrapperStyle, 'test-Button-loadingIcon')}>
            <Spinner size="small" />
          </div>
        )}
      </UnstyledButton>
    );
  },
);

// With `forwardRef`, this component would show in render trees as `<ForwardRef />`.
// Setting a displayName overrides this behaviour.
Button.displayName = 'Button';

Button.defaultProps = {
  analyticsMetadata: {},
  className: '', // TO BE DEPRECATED EN-2430
  elementType: 'button',
  form: null,
  href: '',
  iconType: null,
  isBlock: false,
  isEnabled: true,
  isLoading: false,
  label: 'Submit',
  onClick: () => {},
  rel: null,
  styleAtSmall: 'fullWidth',
  styleType: 'primary',
  target: null,
  testId: null,
  title: '',
  type: 'submit',
};

Button.propTypes = {
  /**
   * Additional metadata that we want to attach to the analytics event on click.
   *
   * Where possible, use existing properties to convey your metadata. In order
   * to maintain consistency across our events, any new properties should be
   * added to this shape.
   *
   * All properties are optional.
   */
  analyticsMetadata: analyticsMetadataPropTypes,

  /**
   * Supplementary styles to apply on this button.
   * TODO - BE DEPRECATED EN-2430
   */
  className: PropTypes.string,

  /**
   * The type of the Button.: a for links, button for actions, OutboundLink for an a that leads off-site.
   */
  elementType: PropTypes.oneOf(['a', 'button', 'OutboundLink']),

  /**
   * An ID of a form to associate, if this is a `<button />` and the form isn't an ancestor.
   *
   * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes
   */
  form: PropTypes.string,

  /**
   * href attribute. Only applies to <a> elementTypes.
   */
  href: PropTypes.string,

  /**
   * A MaterialIcon iconType to show on this button.
   */
  iconType: PropTypes.string,

  /**
   * Should this button always fill its container, regardless of styleAtSmall?
   */
  isBlock: PropTypes.bool,

  /**
   * Show a slightly-less-padded button. Preferred in places where we have less
   * real estate to play with.
   */
  isCompact: PropTypes.bool,

  /**
   * Is this button enabled? Propagates as <button>'s `disabled` attribute.
   * Only applies to <button> elements.
   */
  isEnabled: PropTypes.bool,

  /**
   * Is this button showing a loading state? Supercedes the icon prop.
   */
  isLoading: PropTypes.bool,

  /**
   * Main text of the button.
   */
  label: PropTypes.string,

  /**
   * Callback when this button activates.
   */
  onClick: PropTypes.func,

  /**
   * rel attribute. Only applies to <a> elements.
   */
  rel: PropTypes.string,

  /**
   * target attribute. Only applies to <a> elements.
   */
  target: PropTypes.oneOf(['_self', '_blank', '_parent', '_top']),

  /**
   * The button's title. Note: this is used as the tooltip, not as the label.
   */
  title: PropTypes.string,

  /**
   * The style of this button, specifically for small viewports. Defaults to
   * fullWidth for historical reasons.
   */
  styleAtSmall: PropTypes.oneOf(['auto', 'fullWidth', 'iconOnly']),

  /**
   * The pre-defined style of this button.
   */
  styleType: PropTypes.oneOf(['primary', 'secondary', 'secondaryWhite']),

  /**
   * Optional string to render in a `data-testid` attribute to allow element to
   * be found in tests
   */
  testId: PropTypes.string,

  /**
   * Default behaviour of the button. Only applies to <button> elements.
   */
  type: PropTypes.oneOf(['button', 'reset', 'submit']),
};

export default Button;
